import { ChangeEvent, FC, MouseEvent } from "react";
import { useIntl } from "react-intl";
import { Box, IconButton, LabelDisplayedRowsArgs, TablePagination, useTheme } from "@mui/material";
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from "@mui/icons-material";

export interface ITablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const TablePaginationActions = (props: ITablePaginationActionsProps) => {
  const { count, page, rowsPerPage, onPageChange } = props;
  const theme = useTheme();

  const handleFirstPageButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
};

export interface IStyledTablePagination {
  rowsLength: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  rowsPerPageOptions?: number[];
  labelRowsPerPage?: string;
  colSpan?: number;
  showAllOption?: boolean;
}

export const StyledTablePagination: FC<IStyledTablePagination> = props => {
  const { formatMessage } = useIntl();
  const {
    labelRowsPerPage = formatMessage({ id: "components.pagination.rowsPerPage" }),
    onPageChange,
    onRowsPerPageChange,
    rowsLength,
    rowsPerPage,
    rowsPerPageOptions = [5, 15, 25],
    page,
    colSpan = 3,
    showAllOption = true,
  } = props;

  const getLabelDisplayedRows = ({ from, to, count }: LabelDisplayedRowsArgs): string => {
    const of = formatMessage({ id: "components.pagination.of" });
    const moreThan = formatMessage({ id: "components.pagination.moreThan" });
    return `${from}–${to} ${of} ${count !== -1 ? count : `${moreThan} ${to}`}`;
  };

  return (
    <TablePagination
      sx={{
        overflow: "visible",
        border: "none",
        "& .MuiTablePagination-spacer": {
          display: "none",
        },
        "& .MuiToolbar-root.MuiTablePagination-toolbar p": {
          fontSize: "10px",
        },
        "& .MuiTablePagination-displayedRows": {
          minWidth: "76px",
          textAlign: "right",
        },
      }}
      rowsPerPageOptions={[
        ...rowsPerPageOptions,
        ...(showAllOption
          ? [{ label: formatMessage({ id: "components.pagination.all" }), value: -1 }]
          : [{ label: "50", value: 50 }]),
      ]}
      labelRowsPerPage={labelRowsPerPage}
      labelDisplayedRows={getLabelDisplayedRows}
      colSpan={colSpan}
      count={rowsLength}
      rowsPerPage={rowsPerPage}
      page={page}
      SelectProps={{
        inputProps: {
          "aria-label": "rows per page",
        },
        native: false,
      }}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      ActionsComponent={TablePaginationActions}
    />
  );
};
